/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      if (this.dataset.empty === 'false') this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.currentTotalItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
        (total, quantityInput) => total + parseInt(quantityInput.value, 10),
        0
      );

      this.currentQuantities = [];
      this.querySelectorAll('.cart-item').forEach((item) => {
        this.currentQuantities[item.dataset.variantId] = Number(item.querySelector('.qty-input__input').value);
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const control = evt.target.closest('.js-remove-item');
      if (!control) return;
      evt.preventDefault();
      this.updateQuantity(control.dataset.index, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.dataset.index, evt.target.value, document.activeElement.name);
    }

    /**
     * Updates the quantity of a line item.
     * @param {number} line - Line item index.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    updateQuantity(line, quantity, name) {
      const row = document.getElementById(`cart-item-${line}`);
      const key = row?.dataset.lineKey;
      return window.aonskCartQueue.run(() => {
        const current = key
          ? Array.from(document.querySelectorAll('.cart-item')).find((item) => item.dataset.lineKey === key)
          : document.getElementById(`cart-item-${line}`);
        if (!current) return;
        const owner = current.closest('cart-items');
        return owner.changeCartQuantity(current.id.replace('cart-item-', ''), quantity, name, key);
      });
    }

    async changeCartQuantity(line, quantity, name, key) {
      if (Shopify.designMode) this.cartDrawer = document.getElementById('cart-drawer');

      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;
      const mutationToken = this.cartDrawer?.beginCartMutation?.('quantity-change') ?? null;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        ...(key ? { id: key } : { line }),
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const lineItem = document.getElementById(`cart-item-${line}`);
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);
        let response;
        try {
          response = await fetch(theme.routes.cartChange, {
            ...this.fetchRequestOpts,
            signal: controller.signal
          });
        } finally {
          window.clearTimeout(timeout);
        }
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);
        if (this.cartDrawer && typeof this.cartDrawer.isCartMutationCurrent === 'function' && !this.cartDrawer.isCartMutationCurrent(mutationToken)) return;

        const newTotalQuantity = data.item_count;

        if (this.cartDrawer) {
          const rendered = await this.cartDrawer.renderContents(data, false, mutationToken);
          if (!rendered) {
            this.cartDrawer.setAttribute('data-cart-refresh-failed', 'true');
            throw new Error(theme.strings.cartError);
          }
          const currentItems = this.cartDrawer.querySelector('cart-items');
          if (newTotalQuantity > 0) currentItems?.setFocus(line, newTotalQuantity, name);
          else if (this.cartDrawer.hasAttribute('open')) trapFocus(this.cartDrawer);
          document.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: { variantId, oldQuantity: this.currentQuantities[variantId], newQuantity: Number(quantity) }
          }));
          return;
        }

        if (this.cartDrawer) {
          this.cartDrawer.dataset.cartEmpty = String(newTotalQuantity === 0);
          cartDrawerContent.classList.toggle('drawer__content--flex', newTotalQuantity === 0);

          if (newTotalQuantity === 0) {
            const recommendations = this.cartDrawer.querySelector('product-recommendations');
            if (recommendations) recommendations.remove();
          }
        } else if (newTotalQuantity === 0) {
          // We're on the Cart page
          const cartTitle = this.closest('.cc-main-cart').querySelector('.js-cart-title');
          if (cartTitle) cartTitle.style.textAlign = 'center';

          const cartSummaryCss = document.getElementById('cart-summary-css');
          if (cartSummaryCss) cartSummaryCss.remove();

          const cartSummary = document.getElementById('cart-summary');
          if (cartSummary) cartSummary.hidden = true;
        }

        this.getSectionsToRender().forEach((section) => {
          if (this.cartDrawer && typeof this.cartDrawer.isCartMutationCurrent === 'function' && !this.cartDrawer.isCartMutationCurrent(mutationToken)) return;
          const sectionHtml = data.sections?.[section.section];
          if (typeof sectionHtml !== 'string') return;

          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          const replacement = CartItems.getElementHTML(sectionHtml, selector);
          if (replacement === null) return;
          el.innerHTML = replacement;

          // Update the cart drawer quantity
          if (section.id === 'cart-icon-bubble') {
            const drawerCount = document.getElementById('cart-drawer__count');
            if (drawerCount) {
              const html = CartItems.getElementHTML(
                sectionHtml,
                `${section.selector} .header__cart-count-number`
              );

              if (html !== null) drawerCount.innerHTML = `(${html})`;
            }
          }
        });

        if (this.cartDrawer && newTotalQuantity === 0) {
          cartDrawerContent.classList.add('grow', 'flex', 'items-center');

          if (this.cartDrawer.querySelector('promoted-products')) {
            this.cartDrawer
              .querySelector('.drawer__content')
              .classList.toggle('drawer__empty-with-promotions', newTotalQuantity === 0);
          }
        }

        window.initLazyImages();
        this.updateRecommendations(data.item_count > 0 ? data.items[0].product_id : null);
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId,
              oldQuantity: this.currentQuantities[variantId],
              newQuantity: Number(quantity)
            }
          }));
        }

        this.currentQuantities[variantId] = Number(quantity);

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (error.name === 'AbortError' || error instanceof TypeError || error instanceof SyntaxError || /^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        if (input) {
          input.value = input.dataset.initialValue;
          input.closest('quantity-input').currentQty = input.dataset.initialValue;
        }
      } finally {
        this.classList.remove('pointer-events-none');
        if (this.cartDrawer) this.cartDrawer.endCartMutation?.(mutationToken);

        // Attempt to maintain the same scroll position in the cart drawer
        const currentContent = this.cartDrawer?.querySelector('.drawer__content');
        if (currentContent) {
          requestAnimationFrame(() => { currentContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { currentContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    refreshCartItems(existingMutationToken = null) {
      if (existingMutationToken !== null) return this.readCartItems(existingMutationToken);
      return window.aonskCartQueue.run(() => this.readCartItems());
    }

    async readCartItems(existingMutationToken = null) {
      // Refresh the entire drawer together, including empty-state wrappers and totals.
      if (this.cartDrawer) return this.cartDrawer.readCartDrawer();
      const errors = document.getElementById('cart-errors');
      const ownsMutation = this.cartDrawer && typeof this.cartDrawer.beginCartMutation === 'function' && existingMutationToken === null;
      const mutationToken = ownsMutation
        ? this.cartDrawer.beginCartMutation('cart-items-refresh')
        : existingMutationToken;
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 12000);
        let response;
        try {
          response = await fetch(`?sections=${[...new Set(sections)]}`, {
            cache: 'no-store',
            signal: controller.signal
          });
        } finally {
          window.clearTimeout(timeout);
        }

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          await this.cartDrawer.readCartDrawer(true);
          return;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);
        if (this.cartDrawer && typeof this.cartDrawer.isCartMutationCurrent === 'function' && !this.cartDrawer.isCartMutationCurrent(mutationToken)) return;

        this.getSectionsToRender().forEach((section) => {
          if (this.cartDrawer && typeof this.cartDrawer.isCartMutationCurrent === 'function' && !this.cartDrawer.isCartMutationCurrent(mutationToken)) return;
          const sectionHtml = data[section.section];
          if (typeof sectionHtml !== 'string') return;

          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          const replacement = CartItems.getElementHTML(sectionHtml, section.selector);
          if (replacement === null) return;
          el.innerHTML = replacement;

          if (section.id === 'cart-items') {
            el.setAttribute('data-empty', !!el.querySelector('.js-cart-empty'));
          }
        });

        const firstCartItem = this.querySelector('.cart-item:first-child');
        this.updateRecommendations(firstCartItem ? firstCartItem.dataset.productId : null);

        window.initLazyImages();

        errors.innerHTML = '';
        errors.hidden = true;
      } catch (error) {
        errors.textContent = theme.strings.cartError;
        errors.hidden = false;
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
      } finally {
        if (ownsMutation && this.cartDrawer) this.cartDrawer.endCartMutation?.(mutationToken);
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];

        // Append any quick order lists to the sections
        document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
          const closestSection = quickOrderList.closest('.shopify-section');
          if (closestSection) {
            const sectionId = closestSection.id.replace('shopify-section-', '');
            sections.push({
              id: `quick-order-list_${sectionId}`,
              section: sectionId,
              selector: `#quick-order-list_${sectionId}`
            });
          }
        });
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          },
          {
            id: 'cart-summary',
            section: this.dataset.section,
            selector: '.cart__summary'
          },
          {
            id: 'aonsk-cart-sale-notice',
            section: this.dataset.section,
            selector: '#aonsk-cart-sale-notice'
          }
        ];
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      if (typeof html !== 'string') return null;
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : null;
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = this.recommendations || document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        this.recommendations.innerHTML = '';
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          trapFocus(
            this.cartDrawer.querySelector('.js-cart-empty'),
            this.cartDrawer.querySelector('a')
          );
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}



